import ScLink from 'components/Generic/SitecoreWrappers/ScLink';
import { LinkField, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { useI18n } from 'next-localization';
import { useEffect, useMemo } from 'react';

import PromotedContent from 'components/Generic/Header/MegaNavigation/PromotedContent';
import { NavItem } from './MegaNavigation.types';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import styles from './megaNavigationBody.module.scss';
import useWindowScrollToggle from 'hooks/useToggleWindowScroll';

type MegaNavigationBodyProps = {
  active: boolean;
  category: NavItem;
  closeMegaNavBody: () => void;
};

const MegaNavigationBody = ({ active, category, closeMegaNavBody }: MegaNavigationBodyProps) => {
  const { t } = useI18n();
  const { sitecoreContext } = useSitecoreContext();
  const { toggleWindowScroll } = useWindowScrollToggle();

  const pullOutLink: LinkField | null =
    category?.fields?.pulloutLink?.value?.href || sitecoreContext?.pageEditing
      ? category?.fields?.pulloutLink
      : null;

  const columns = category?.fields?.items || [];

  const promotedBlock = useMemo(() => {
    return category?.fields?.promotedContent && columns.length < 5
      ? category?.fields?.promotedContent || {}
      : null;
  }, [category?.fields, columns.length]);

  const columnsClass = useMemo(() => {
    let columnsLength: number = columns.length;
    if (promotedBlock) {
      columnsLength += 1;
    }
    return columnsLength >= 5 ? 'col-lg' : 'col-lg-3';
  }, [columns.length, promotedBlock]);

  useEffect(() => {
    if (active) {
      toggleWindowScroll(true);
    }

    return () => toggleWindowScroll(false);
  }, [active]);

  return (
    <div className={`d-none d-xl-block background-white ${styles.meganav__body}`}>
      <div className={`${styles.meganav__body__inner} pt-4 pb-8`}>
        <div className="container">
          <div className={`d-flex mb-4 ${styles.meganav__heading}`}>
            {pullOutLink !== null && (
              <ScLink
                field={pullOutLink}
                replaceWithSlot={true}
                aria-hidden={active ? 'false' : 'true'}
                tabIndex={active ? 0 : 1}
                className="meganav-link meganav-link--small revert-underline"
              />
            )}
            <button
              onClick={closeMegaNavBody}
              tabIndex={active ? 0 : -1}
              aria-hidden={active ? 'false' : 'true'}
              className={`btn btn-naked text-link ms-auto ${styles.meganav__closer}`}
            >
              <span>{t('close')}</span>
              <i className="bi bi-x fs-3" />
            </button>
          </div>
          <section className="row flex-nowrap">
            {columns.map((column, index) => (
              <div key={index} className={columnsClass} data-index={index}>
                <div className={styles.meganav__column}>
                  <Text
                    field={column.fields.title}
                    tag="p"
                    className="meganav-link meganav-link--large"
                  />
                  <Text
                    field={column.fields.description}
                    tag="p"
                    className="body-small text-grey mt-2"
                  />
                  <ul className={styles['meganav__column__list']}>
                    {column.fields.items?.map((linkItem, idx) => (
                      <li key={idx} className="mt-3">
                        <ScLink
                          field={linkItem.fields.link}
                          className="meganav-link meganav-link--small revert-underline"
                          replaceWithSlot={true}
                          showExternalIcon={true}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
            {promotedBlock && (
              <PromotedContent
                fields={promotedBlock.fields}
                e2eId={promotedBlock.id}
                isWide={columnsClass === 'col-lg-3'}
                className={`ms-auto ${columnsClass} ${styles.content__wrapper}`}
                closeMegaNavBody={closeMegaNavBody}
              />
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default MegaNavigationBody;
